import type { AlanHatasi, HataKodu, HataYanit } from './tipler';

/**
 * API HATASI.
 *
 * `fetch` yalnız ağ kopmasında reddeder; 4xx/5xx yanıtları başarılı sayar.
 * Bu sınıf sayesinde istek katmanı hataları `throw` eder ve TanStack Query
 * ile `try/catch` doğal biçimde çalışır.
 *
 * Ağ hatası da buraya sarılır: çağıran taraf "sunucu ne dedi" ile "sunucuya
 * ulaşılamadı" ayrımını `durum === 0` ile yapar. İki ayrı hata tipi olsaydı
 * her `catch` bloğunda iki dal gerekirdi.
 */
export class ApiHatasi extends Error {
  readonly baslik: string;
  readonly durum: number;
  readonly kod?: HataKodu;
  readonly yol: string;
  readonly zaman: string;
  readonly alanlar?: AlanHatasi[];

  constructor(yanit: HataYanit) {
    super(yanit.mesaj);
    this.name = 'ApiHatasi';
    this.baslik = yanit.baslik;
    this.durum = yanit.durum;
    this.kod = yanit.kod;
    this.yol = yanit.yol;
    this.zaman = yanit.zaman;
    this.alanlar = yanit.alanlar;
  }

  /** Sunucuya hiç ulaşılamadı (ağ kopması, CORS, iptal). */
  get agHatasiMi(): boolean {
    return this.durum === 0;
  }

  /** Alan bazlı doğrulama hatası — form altına yazdırılabilir. */
  get alanHatalari(): Record<string, string[]> {
    const sonuc: Record<string, string[]> = {};
    for (const a of this.alanlar ?? []) sonuc[a.alan] = a.hatalar;
    return sonuc;
  }

  static agKopmasi(yol: string): ApiHatasi {
    return new ApiHatasi({
      baslik: 'Bağlantı kurulamadı',
      mesaj: 'Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edin.',
      durum: 0,
      yol,
      zaman: new Date().toISOString(),
    });
  }
}

/** Gövdesi okunamayan yanıtlar için son çare. Sunucu HTML hata sayfası
 *  döndürdüğünde (nginx 502 gibi) JSON ayrıştırması başarısız olur. */
export function bicimsizYanitHatasi(durum: number, yol: string): ApiHatasi {
  return new ApiHatasi({
    baslik: 'Beklenmeyen yanıt',
    mesaj: `Sunucu anlaşılamayan bir yanıt döndürdü (HTTP ${durum}).`,
    durum,
    yol,
    zaman: new Date().toISOString(),
  });
}

export function apiHatasiMi(deger: unknown): deger is ApiHatasi {
  return deger instanceof ApiHatasi;
}
